import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Paper,
} from "@material-ui/core";
import { SrButton, SrInput } from "../../../../common";
import translate from "../../../../../i18n/translate";

import amex from "../../../../../assets/images/paycard/paycard-amex.png";
import carnet from "../../../../../assets/images/paycard/paycard-carnet.png";
import mastercard from "../../../../../assets/images/paycard/paycard-mastercard.png";
import visa from "../../../../../assets/images/paycard/paycard-visa.png";

const CARDS = [
  { id: "VISA", src: visa, alt: "visa" },
  { id: "MAST", src: mastercard, alt: "mastercard" },
  { id: "AMEX", src: amex, alt: "american express" },
  { id: "CRNT", src: carnet, alt: "carnet" },
];

const CHARGE_METHODS = [
  { id: "POS", label: translate("transactions.cards") },
  {
    id: "TRA",
    label: translate("transactions.wireTransfer"),
  },
  {
    id: "OXX",
    label: translate("transactions.shopOrBank"),
  },
  { id: "SPE", label: translate("transactions.spei") },
];

const CARDS_ID = CHARGE_METHODS[0].id;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: theme.spacing(3),
  },
  cardsBox: {
    overflow: "hidden",
  },
  cardButton: {
    minWidth: "auto",
    width: "45px",
    padding: "6px 8px",
    "&.selected": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  checkboxGridContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
  title: {
    marginBottom: theme.spacing(1),
  },
}));

const Filters = React.forwardRef(
  (
    {
      selectedChargeMethods,
      onCheckboxChange,
      operators,
      selectedOperator,
      onOperatorChange,
      onApplyFilters,
      onCleanFilters,
      selectedCard,
      onCardClick,
    },
    ref
  ) => {
    const classes = useStyles();
    const isCardsSelectorVisible = selectedChargeMethods.some(
      (id) => id === CARDS_ID
    );

    return (
      <Paper elevation={6} ref={ref}>
        <Box
          className={classes.wrapper}
          display="grid"
          gridTemplateRows="max-content"
          alignContent="strecht"
          gridRowGap={25}
        >
          <Box>
            <Typography className={classes.title}>
              {translate("transactions.channels")}
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr">
              {CHARGE_METHODS.map((checkbox) => (
                <FormControlLabel
                  key={checkbox.id}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedChargeMethods.some(
                        (id) => id === checkbox.id
                      )}
                      onChange={(evt) => {
                        onCheckboxChange(evt);
                        if (evt.target.name === CARDS_ID && !evt.target.checked)
                          onCardClick();
                      }}
                      name={checkbox.id}
                    />
                  }
                  label={checkbox.label}
                />
              ))}
            </Box>
            <AnimatePresence>
              {isCardsSelectorVisible && (
                <motion.div
                  className={classes.cardsBox}
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box marginTop="15px">
                    <Typography className={classes.title}>
                      {translate("transactions.typeOfCard")}
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns="1fr 1fr 1fr 1fr"
                      gridColumnGap={5}
                      justifyItems="centrer"
                    >
                      {CARDS.map(({ id, src, alt }) => (
                        <SrButton
                          key={id}
                          className={[
                            classes.cardButton,
                            id === selectedCard ? "selected" : "",
                          ].join(" ")}
                          variant="outlined"
                          onClick={() => onCardClick(id)}
                        >
                          <img src={src} width="24" alt={alt} />
                        </SrButton>
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <Box>
            <Typography className={classes.title}>
              {translate("transactions.operators")}
            </Typography>
            <SrInput
              size="small"
              select
              fullWidth
              variant="outlined"
              value={selectedOperator}
              onChange={onOperatorChange}
            >
              {operators.map((operator) => (
                <MenuItem
                  className="filter-item"
                  key={operator.id}
                  value={operator.id}
                >
                  {operator.name}
                </MenuItem>
              ))}
            </SrInput>
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <SrButton size="small" onClick={onCleanFilters}>
              {translate("transactions.removeFilters")}
            </SrButton>
            <Box marginLeft={1}>
              <SrButton
                size="small"
                variant="contained"
                color="primary"
                onClick={onApplyFilters}
              >
                {translate("transactions.applyFilters")}
              </SrButton>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  }
);

export default Filters;
